import '../../../scss/hotfood/components/hotfood-fable.scss'

import {
  Body1,
  Box,
  Button,
  ButtonGroup,
  Display1,
  Flex,
  Grid,
  GridItem,
  Label1,
  Modal,
} from '@sainsburys-tech/fable'
import { InfoCircle } from '@sainsburys-tech/icons'
import React, { useEffect, useState } from 'react'

import { FormErrorMessageKey } from '../../enums/FormErrorMessageKey'
import { TempCheckFor } from '../../enums/TempCheckFor'
import ICounterCheck from '../../types/ICounterCheck'
import { ITemperatureCheckValidationProps } from '../../types/ITemperatureCheckValidationProps'
import { ITemperatureData } from '../../types/ITemperatureData'
import {
  getInstructionDescription,
  getInstructionTitle,
} from '../../utils/CounterInstructionMessages'
import { allTemperaturesExist, isAM } from '../../utils/hotFoodUtils'
import TemperatureCheckErrorMessage from '../common/TemperatureCheckErrorMessage'
import CounterTempCheckInputCard from './CounterTempCheckInputCard'

interface IProps {
  cabinet: ICounterCheck
  handleClose: () => void
  handleCloseStillNotFixed: () => void
  handleSave: (data: ICounterCheck) => Promise<void>
  isOpen: boolean
  isBluetoothReading: boolean
}

const CabinetStatusModal = (props: IProps) => {
  const [temperatures, setTemperatures] = useState<ITemperatureData[]>([])
  const [validation, setValidation] =
    useState<ITemperatureCheckValidationProps>({
      errorData: [],
      isLowReadingWarned: false,
      messageKey: FormErrorMessageKey.NONE,
      result: true,
    })

  const maxShelfNumber = Math.max(
    ...(props.cabinet.shelves?.map((shelf) => shelf.shelfNumber) || []),
  )

  const onSubmitReading = async () => {
    // ensure that all temperatures have a value, whether valid or not
    const [errorData, messageKey, result] = allTemperaturesExist(temperatures)
    setValidation({
      errorData: errorData as ITemperatureData[],
      isLowReadingWarned: false,
      messageKey: messageKey as FormErrorMessageKey,
      result: result as boolean,
    })

    if (!result) {
      return
    }

    let valid = true

    for (let count = 1; count <= temperatures.length; count++) {
      const temp = temperatures.find((t) => t.position === count)!.temperature
      const tempRange = props.cabinet.shelves!.find(
        (s) => s.shelfNumber === count && s.isAfternoon === !isAM(),
      )!

      valid = valid && Number(temp) >= tempRange.minimumTemperature
    }

    if (!valid) {
      props.handleCloseStillNotFixed()
    } else {
      await props.handleSave({ ...props.cabinet, temperatures })
    }
  }

  useEffect(() => {
    if (maxShelfNumber === 5) {
      setTemperatures([
        {
          position: 1,
          temperature: '',
        },
        {
          position: 2,
          temperature: '',
        },
        {
          position: 3,
          temperature: '',
        },
        {
          position: 4,
          temperature: '',
        },
        {
          position: 5,
          temperature: '',
        },
      ])
    } else {
      setTemperatures([
        {
          position: 1,
          temperature: '',
        },
        {
          position: 2,
          temperature: '',
        },
        {
          position: 3,
          temperature: '',
        },
        {
          position: 4,
          temperature: '',
        },
      ])
    }
  }, [maxShelfNumber])

  return (
    <Modal
      size={props.cabinet.isFaulty ? 'sm' : 'md'}
      onClose={props.handleClose}
      isOpen={props.isOpen}
      testid='info-modal'
      header={{
        label: props.cabinet.isFaulty
          ? 'Faulty cabinet'
          : 'Mark cabinet as fixed',
      }}
      className='c-cabinet-status-modal'
    >
      <Box padding='none'>
        {props.cabinet.isFaulty ? (
          <Box as='span' padding='none'>
            <Body1>
              Are you sure this cabinet is faulty? By marking it as faulty you
              are confirming it is not being used and there is an open work
              order for the fixture on FM Assist.
            </Body1>
          </Box>
        ) : (
          <Flex direction='column' gap='md'>
            <Box as='span' padding='none'>
              <Body1>
                Please only mark cabinet as fixed once it's back up to
                temperature and operating correctly. You'll also need to
                complete an air temperature check before using the counter.
              </Body1>
            </Box>

            <Grid cols={12} gap='md'>
              <GridItem colSpan={{ default: 12 }}>
                <Box padding='none'>
                  <Display1 style={{ marginBottom: '8px' }}>
                    {getInstructionTitle(TempCheckFor.FirstAir)}
                  </Display1>
                </Box>
                <Box padding='none'>
                  <Body1 style={{ marginBottom: '8px' }}>
                    {getInstructionDescription(TempCheckFor.FirstAir)}
                  </Body1>
                </Box>
              </GridItem>
            </Grid>

            <Box className='hotfood-full-width' padding='none'>
              <Display1>{`Turbo-serve ${props.cabinet.counterNumber}`}</Display1>
            </Box>

            {!props.isBluetoothReading && (
              <Flex alignItems='center' gap='md'>
                <InfoCircle
                  className='c-info-icon'
                  style={{ color: 'var(--fable-color-text-new)' }}
                />
                <Box data-testid='manual-input-hint' padding='none'>
                  <Body1>
                    You've changed to manual checks. Please manually input the
                    temperature checks below.
                  </Body1>
                </Box>
              </Flex>
            )}

            <Grid cols={12} gap='md'>
              {temperatures.map((item, index) => (
                <GridItem colSpan={{ xs: 12, sm: 6, md: 6, lg: 6 }} key={index}>
                  <Flex direction='row' alignItems='center' gap='sm'>
                    <Label1 className='c-counter-temp-label'>
                      {(() => {
                        switch (item.position) {
                          case 1:
                            return 'Top'
                          case 2:
                            return '2nd'
                          case 3:
                            return '3rd'
                          case 4:
                            return '4th'
                          case 5:
                            return '5th'
                          default:
                            return ''
                        }
                      })()}
                    </Label1>
                    <CounterTempCheckInputCard
                      className='c-cabinet-status-modal-temperature-input'
                      counterNumber={props.cabinet.counterNumber}
                      hasError={
                        validation.errorData.filter(
                          (t) => t.position === item.position,
                        ).length > 0
                      }
                      key={index}
                      currentTemp={item}
                      temperatures={temperatures}
                      setTemperatures={setTemperatures}
                      isModal={true}
                      isBluetoothReading={props.isBluetoothReading}
                    />
                  </Flex>
                </GridItem>
              ))}
            </Grid>
          </Flex>
        )}

        {!validation.result && (
          <Box style={{ marginTop: '1rem' }} padding='none'>
            <TemperatureCheckErrorMessage
              class=''
              messageKey={validation.messageKey}
            />
          </Box>
        )}

        <Box as='div' padding='none' style={{ marginTop: '16px' }}>
          <ButtonGroup gap='lg' orientation='horizontal'>
            <Button
              isFullWidth={true}
              onClick={() =>
                props.cabinet.isFaulty
                  ? props.handleSave(props.cabinet)
                  : onSubmitReading()
              }
              testid='mark-as-button'
              style={{ width: '100%' }}
            >
              Mark as{' '}
              {props.cabinet.isFaulty ? 'faulty' : 'fixed and submit reading'}
            </Button>
            <Button
              isFullWidth={true}
              testid='close-button'
              style={{
                backgroundColor: 'var(--fable-color-background)',
                width: '100%',
              }}
              onClick={() => props.handleClose()}
              variant='secondary'
            >
              Cancel
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </Modal>
  )
}

export default CabinetStatusModal
